import React, { useState, useEffect, useCallback, createContext, useMemo, MutableRefObject, ReactNode } from 'react'
import {
    GridColDef,
    useGridApiRef,
    GridApi,
    GridSelectionModel,
    DataGridProProps,
    GridPinnedColumns,
    GridRowHeightParams,
    GridRowHeightReturnValue,
    GridPreferencePanelsValue,
    GridLinkOperator,
    GridSortItem,
    GridRowClassNameParams,
    GridPinnedRowsProp,
} from '@mui/x-data-grid-pro'
import { SubmissionContainer } from 'components/Submissions/Review/Submissions/SubmissionList'
import HeaderBar from './HeaderBar'
import FilterAndSortBar, { FilterItem } from './FilterAndSortBar'
import { Box } from 'rebass'
import { theme } from 'theme'
import MuiTable, { TableViewType } from 'components/Shared/MuiTable'
import { RowsProps as SceenshotRowsProps } from 'components/Submissions/Review/Submissions/ReviewTable/ScreenshotTableDef'
import { RowsProps as PostRowsProps } from 'components/Submissions/Review/Submissions/ReviewTable/PostTableDef'
import { RowsProps as UrlRowsProps } from 'components/Submissions/Review/Submissions/ReviewTable/UrlTableDef'
import {
    WaitListTableRows,
    CompletedTimeTableRows,
    RejectedTableRows,
    DeclinedRegistrationResultsTableRows,
    DropoutsTableRows,
    RegistrationResultTableRows,
} from 'components/Submissions/Review/Submissions/SimpleTable/constants'
import { RowsProps as BannerGroupsRowsProps } from 'components/Banners/BannerGroups/BannerGroupsTableDef'
import { RowsProps as RolesRowsProps } from 'components/Team/Teams/Roles'
import { RowsProps as NoneSubmitTableRowsProps } from 'components/Submissions/Review/Submissions/ReviewTable/NoneSubmitStepTableDef'

import { DecisionEditorStates } from 'components/Submissions/Review/common/DecisionEditor'
import {
    selectSelectedCampaignUuid,
    selectSelectedRegistrationView,
    setFilterModel,
    setFilterOptions,
    setMuiTableRef,
    setSortItem,
} from 'state/review'
import {
    setSortItem as setAmbassadorSortItem,
    setFilterOptions as setAmbassadorFilterOptions,
    setFilterModel as setAmbassadorFilterModel,
} from 'state/ambassadors'
import { useDispatch, useSelector } from 'react-redux'
import WaitlistHeaderBar from './WaitlistHeaderBar'
import { OptionsState } from 'particles/Dropdown'
import { FIELDS_TYPE, SORT_DIRECTION } from 'components/utils/filterAndSort'
import { isOppositeSortField } from 'components/utils'
import { RegistrationRowProps } from 'components/Submissions/Review/Overview/RegistrationSummaryTableDef'
import { SubmissionRowProps } from 'components/Submissions/Review/Overview/SubmissionSummaryTableDef'
import { HandleMassSubmissionFunction } from 'components/Submissions/Review/common/types'
import { SubmissionFilterChip } from 'components/Submissions/Review/Submissions/Toolbar/Filter'
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro'
import styled from 'particles/themes/styled'
import { useAddCustomizeFilter } from 'hooks/displayTooltip'
import { RowsProps as EngagementSceenshotRowsProps } from 'components/Submissions/Review/Submissions/ReviewTable/EngagementScreenshotTableDef'
import { RowsProps as EngagementPostRowsProps } from 'components/Submissions/Review/Submissions/ReviewTable/EngagementPostTableDef'
import { RowsProps as CustomEngagementRowsProps } from 'components/Submissions/Review/Submissions/ReviewTable/CustomEngagementTableDef'
import {
    setIndividualPerformanceSortItem,
    setIndividualPerformanceFilterOptions,
    setIndividualPerformanceFilterModel,
} from 'state/campaignReport'
import { useFilterModel, useFilterOptions, useSortItem } from './hooks'
import { AmbassadorRowProps } from 'components/Ambassadors/Table/rows'
import { CampaignOverviewRowProps } from 'components/Submissions/Review/Report/Tabs/Overview/CampaignOverviewTableDef'
import { CampaignPerformanceRowProps } from 'components/Submissions/Review/Report/Tabs/Overview/CampaignPerformanceTableDef'
import { TaskPerformanceRowProps } from 'components/Submissions/Review/Report/Tabs/TaskPerformance/TaskPerformanceTableDef'
import { IndividualPerformanceRowProps } from 'components/Submissions/Review/Report/Tabs/IndividualPerformance/IndividualPerformanceTableDef'
import { TopPerformersRowProps } from 'components/Submissions/Review/Report/Tabs/TopPerformers/TopPerformersTableDef'

const StyledSubmissionFilterChip = styled(SubmissionFilterChip)`
    padding: 12px 12px 4px 12px;
`

export interface PreferenceElState {
    anchorEl: null | HTMLElement
    isMoreAction: boolean
}

export interface SortItem extends GridSortItem {
    fieldLabel: string
    sortDirectionLabel: string
}

export interface PartiTableProps {
    title?: string
    rowHeight: number
    columns: GridColDef[]
    rows:
        | DropoutsTableRows[]
        | RejectedTableRows[]
        | DeclinedRegistrationResultsTableRows[]
        | RegistrationResultTableRows[]
        | WaitListTableRows[]
        | CompletedTimeTableRows[]
        | UrlRowsProps[]
        | SceenshotRowsProps[]
        | PostRowsProps[]
        | BannerGroupsRowsProps[]
        | RolesRowsProps[]
        | RegistrationRowProps[]
        | SubmissionRowProps[]
        | EngagementSceenshotRowsProps[]
        | EngagementPostRowsProps[]
        | CustomEngagementRowsProps[]
        | NoneSubmitTableRowsProps[]
        | AmbassadorRowProps[]
        | CampaignOverviewRowProps[]
        | CampaignPerformanceRowProps[]
        | TaskPerformanceRowProps[]
        | IndividualPerformanceRowProps[]
        | TopPerformersRowProps[]
    onClickSubmissionToolbarApprove?: HandleMassSubmissionFunction
    onClickRegistrationToolbarApprove?: (
        ref: MutableRefObject<GridApiPro>,
    ) => (e?: React.MouseEvent<HTMLElement>) => void
    onClickToolbarRequestEdit?: HandleMassSubmissionFunction
    onClickToolbarReject?: HandleMassSubmissionFunction
    onClickBriefRequirement?: () => void
    onClickToolbarMoveToWaitlist?: any
    onClickApprove?: any
    onClickRequestEdit?: any
    onClickReject?: any
    decisionEditorState?: {
        state: DecisionEditorStates | undefined
        data: any | undefined
    }
    onClickMoveBackToPending?: any
    onClickMoveToPrioriyList?: any
    hideApproveOrderButton?: boolean
    isLoading: boolean
    hideToolbar?: boolean // todo:  if too many buttons in future, need to refactor this to support button display
    viewType?: TableViewType
    customEmptyState?: ReactNode
    enableImportList?: boolean
    enableMultiSelect?: boolean
    enableExport?: boolean
    showFilter?: boolean
    showSort?: boolean
    showSearchBar?: boolean
    remainingSlot?: number
    hideTableBorder?: boolean
    tableHeight?: string
    toolBarType?: 'waitlist' | 'pending' | 'moveBackToPending'
    disableMoveBackToPending?: boolean
    disableColumnMenu?: boolean
    displayPagination?: boolean
    hideFooterRowCount?: boolean
    noPadding?: boolean
    pinnedColumns?: GridPinnedColumns
    hideFooter?: boolean
    onClickCheckBox?: (apiRef: React.MutableRefObject<GridApi>, selectionModel: GridSelectionModel) => void
    getRowHeight?: ((params: GridRowHeightParams) => GridRowHeightReturnValue) | undefined
    isNewStyle?: boolean
    showMoreAction?: boolean
    headerHeight?: number
    getRowClassName?: (params: GridRowClassNameParams) => string
    className?: string
    pinnedRows?: GridPinnedRowsProp
}

interface TableWithHeaderBarProps extends PartiTableProps {
    apiRef: React.MutableRefObject<GridApi>
    disableApprove: boolean
    disableMoveToWaitlist: boolean
    disableMoveBackToPending: boolean
    disableMoveToPrioriyList: boolean
    hideApproveOrderButton: boolean
    onShowColumnPanel: any
    onSelectionModelChange: DataGridProProps['onSelectionModelChange']
    enableImportList: boolean
    enableExport: boolean
    noPadding?: boolean
    pinnedColumns?: GridPinnedColumns
    preferenceEl: PreferenceElState
    onClosePreferencePanel: () => void
    filterOptions?: OptionsState[]
    sortItem?: GridSortItem | null
    onRemoveFilter: (value: string, isSort?: boolean) => void
    onShowColumnFilter: (e: any, value: string, isSort?: boolean) => void
    onAddFilterField: (option: OptionsState) => void
    selectedFilterAndSort: { field: string; isSort?: boolean }
    onSelectFilter: (options: OptionsState[], sortOrColumn: FIELDS_TYPE) => void
    onSelectSort: (item: SortItem) => void
    onCloseFilterField: () => void
    onAddSortField: (option: OptionsState) => void
    className?: string
}

interface TableHeaderContextProps {
    columns: GridColDef[]
}

export const TableHeaderContext = createContext({} as TableHeaderContextProps)
interface TableContextProps {
    showFilter?: boolean
    showSort?: boolean
}
export const TableContext = createContext({} as TableContextProps)

const TableWithHeaderBar: React.FC<TableWithHeaderBarProps> = (props): JSX.Element => {
    const {
        title,
        rowHeight,
        columns,
        rows,
        onClickRegistrationToolbarApprove,
        onClickSubmissionToolbarApprove,
        onClickToolbarRequestEdit,
        onClickToolbarReject,
        onClickToolbarMoveToWaitlist,
        onClickApprove,
        onClickRequestEdit,
        onClickReject,
        onClickMoveBackToPending,
        onClickMoveToPrioriyList,
        isLoading,
        hideToolbar,
        viewType,
        customEmptyState,
        enableImportList,
        enableMultiSelect,
        enableExport,
        showFilter,
        showSort,
        showSearchBar,
        apiRef,
        disableApprove,
        disableMoveToWaitlist,
        disableMoveBackToPending,
        disableMoveToPrioriyList,
        onShowColumnPanel,
        onSelectionModelChange,
        tableHeight,
        toolBarType,
        disableColumnMenu,
        hideFooterRowCount,
        displayPagination,
        noPadding,
        pinnedColumns,
        hideFooter,
        getRowHeight,
        isNewStyle,
        showMoreAction,
        preferenceEl,
        onClosePreferencePanel,
        filterOptions,
        sortItem,
        onRemoveFilter,
        onShowColumnFilter,
        onAddFilterField,
        selectedFilterAndSort,
        onSelectFilter,
        onSelectSort,
        onCloseFilterField,
        onAddSortField,
        onClickBriefRequirement,
        getRowClassName,
        headerHeight,
        className,
        pinnedRows,
        ...styleProps
    } = props

    const showFilterAndSortBar = useMemo(() => {
        return toolBarType && !hideToolbar && (filterOptions?.length || sortItem)
    }, [toolBarType, hideToolbar, filterOptions?.length, sortItem])

    return (
        <>
            <TableHeaderContext.Provider value={{ columns }}>
                {toolBarType !== 'waitlist' ? (
                    !hideToolbar ? (
                        <HeaderBar
                            onShowColumnPanel={onShowColumnPanel}
                            onClickSubmissionToolbarApprove={onClickSubmissionToolbarApprove}
                            onClickRegistrationToolbarApprove={
                                onClickRegistrationToolbarApprove
                                    ? () => onClickRegistrationToolbarApprove(apiRef)
                                    : undefined
                            }
                            onClickToolbarReject={onClickToolbarReject}
                            onClickToolbarRequestEdit={onClickToolbarRequestEdit}
                            onClickToolbarMoveToWaitlist={
                                onClickToolbarMoveToWaitlist ? (): void => onClickToolbarMoveToWaitlist(apiRef) : null
                            }
                            onClickMoveBackToPending={
                                onClickMoveBackToPending ? (): void => onClickMoveBackToPending(apiRef) : null
                            }
                            onClickBriefRequirement={onClickBriefRequirement}
                            enableImportList={enableImportList}
                            enableExport={enableExport}
                            showFilter={showFilter}
                            showSort={showSort}
                            showSearchBar={showSearchBar}
                            disableApprove={disableApprove}
                            disableMoveToWaitlist={disableMoveToWaitlist}
                            disableMoveBackToPending={disableMoveBackToPending}
                            showMoreAction={showMoreAction}
                            onAddFilterField={onAddFilterField}
                            onAddSortField={onAddSortField}
                            className="table-header-bar"
                            viewType={viewType}
                        />
                    ) : null
                ) : (
                    <WaitlistHeaderBar
                        onClickMoveBackToPending={
                            onClickMoveBackToPending ? (): void => onClickMoveBackToPending(apiRef) : null
                        }
                        onClickMoveToPrioriyList={
                            onClickMoveToPrioriyList ? (): void => onClickMoveToPrioriyList(apiRef) : null
                        }
                        disableMoveBackToPending={disableMoveBackToPending}
                        disableMoveToPrioriyList={disableMoveToPrioriyList}
                        hideToolbar={hideToolbar}
                        onAddFilterField={onAddFilterField}
                        onAddSortField={onAddSortField}
                    />
                )}
                {showFilterAndSortBar && (
                    <FilterAndSortBar
                        onRemoveFilter={onRemoveFilter}
                        onShowColumnFilter={onShowColumnFilter}
                        selectedFilterAndSort={selectedFilterAndSort}
                        apiRef={apiRef}
                        onSelectFilter={onSelectFilter}
                        onSelectSort={onSelectSort}
                        onCloseFilterField={onCloseFilterField}
                        className="filter-and-sort-bar"
                        viewType={viewType}
                    />
                )}

                {/* TODO: this is temporary chip for submissions filter, adjustment for submission filter will be done later (different ticket) */}
                {viewType && [TableViewType.SUBMISSIONS, TableViewType.PERFORMANCE].includes(viewType) && (
                    <StyledSubmissionFilterChip />
                )}
            </TableHeaderContext.Provider>
            <Box flex={1} p={noPadding ? 0 : theme.spacing.xxs} {...styleProps}>
                <TableContext.Provider value={{ showFilter, showSort }}>
                    <MuiTable
                        apiRef={apiRef}
                        key={title}
                        title={title}
                        rowHeight={rowHeight}
                        columns={columns}
                        rows={rows}
                        onClickApprove={onClickApprove}
                        onClickRequestEdit={onClickRequestEdit}
                        onClickReject={onClickReject}
                        isLoading={isLoading}
                        viewType={viewType}
                        customEmptyState={customEmptyState}
                        enableMultiSelect={enableMultiSelect}
                        onSelectionModelChange={onSelectionModelChange}
                        tableHeight={tableHeight}
                        disableColumnMenu={disableColumnMenu}
                        displayPagination={displayPagination}
                        hideFooterRowCount={hideFooterRowCount}
                        pinnedColumns={pinnedColumns}
                        hideFooter={hideFooter}
                        getRowHeight={getRowHeight}
                        isNewStyle={isNewStyle}
                        preferenceEl={preferenceEl}
                        onClosePreferencePanel={onClosePreferencePanel}
                        onAddFilterField={onAddFilterField}
                        onAddSortField={onSelectSort}
                        getRowClassName={getRowClassName}
                        headerHeight={headerHeight}
                        className={className}
                        pinnedRows={pinnedRows}
                    />
                </TableContext.Provider>
            </Box>
        </>
    )
}

const PartiTable: React.FC<PartiTableProps> = ({
    hideToolbar = true,
    enableImportList = false,
    enableMultiSelect = false,
    enableExport = false,
    showFilter = false,
    showSort = false,
    hideTableBorder = false,
    toolBarType = 'pending',
    noPadding = false,
    onClickCheckBox,
    pinnedColumns,
    hideFooter = false,
    hideFooterRowCount = false,
    hideApproveOrderButton = false,
    tableHeight,
    viewType,
    getRowClassName,
    headerHeight,
    pinnedRows,
    customEmptyState,
    ...props
}) => {
    const dispatch = useDispatch()
    const apiRef = useGridApiRef()
    const [disableApprove, setDisableApprove] = useState<boolean>(true)
    const [preferenceEl, setPreferenceEl] = useState<PreferenceElState>({ anchorEl: null, isMoreAction: false })
    const [disableMoveToWaitlist, setDisableMoveToWaitlist] = useState<boolean>(true)
    const [disableMoveBackToPending, setDisableMoveBackToPending] = useState<boolean>(true)
    const [disableMoveToPrioriyList, setDisableMoveToPrioriyList] = useState<boolean>(true)
    const [, handleOnClickPartiposterTags] = useAddCustomizeFilter()
    const campaignUuid = useSelector(selectSelectedCampaignUuid)
    const registrationViewType = useSelector(selectSelectedRegistrationView)
    const filterOptions = useFilterOptions(viewType)
    const filterModel = useFilterModel(viewType)
    const filters: Array<FilterItem> = filterModel && filterModel.items ? ([...filterModel.items] as FilterItem[]) : []
    const sortItem = useSortItem(viewType)
    const [selectedFilterAndSort, setSelectedFilterAndSort] = useState<{
        field: string
        isSort?: boolean
        hideTooltip?: boolean
    }>({
        field: sortItem ? sortItem.field : '',
        isSort: Boolean(sortItem?.field),
        hideTooltip: Boolean(sortItem?.field),
    })

    const handleAddFilterField = (option: OptionsState) => {
        if (viewType && [TableViewType.SUBMISSIONS, TableViewType.PERFORMANCE].includes(viewType)) {
            if (option?.value === FIELDS_TYPE.PARTIPOSTER_TAGS) {
                handleOnClickPartiposterTags()
            }
        } else if (viewType === TableViewType.INDIVIDUAL_PERFORMANCE) {
            const isDuplicate = filterOptions.some(existingOption => existingOption.value === option.value)
            if (!isDuplicate) {
                const newFilterOptions = [...filterOptions]
                newFilterOptions.unshift(option)

                dispatch(setIndividualPerformanceFilterOptions(newFilterOptions))
            }
            setSelectedFilterAndSort({ field: option.value as string })
        } else if (viewType === TableViewType.AMBASSADOR) {
            const isDuplicate = filterOptions.some(existingOption => existingOption.value === option.value)
            if (!isDuplicate) {
                const newFilterOptions = [...filterOptions]
                newFilterOptions.unshift(option)

                dispatch(setAmbassadorFilterOptions(newFilterOptions))
            }
            setSelectedFilterAndSort({ field: option.value as string })
        } else {
            const isDuplicate = filterOptions.some(existingOption => existingOption.value === option.value)
            if (!isDuplicate) {
                if (campaignUuid && registrationViewType) {
                    const newFilterOptions = [...filterOptions]
                    newFilterOptions.unshift(option)
                    dispatch(
                        setFilterOptions({
                            viewType: registrationViewType,
                            campaignUuid,
                            options: newFilterOptions,
                        }),
                    )
                }
            }
            setSelectedFilterAndSort({ field: option.value as string })
        }
    }

    const handleAddSortField = (item: SortItem, hideTooltip?: boolean) => {
        if (viewType === TableViewType.INDIVIDUAL_PERFORMANCE) {
            dispatch(
                setIndividualPerformanceSortItem({
                    options: item,
                }),
            )
        } else if (viewType === TableViewType.AMBASSADOR) {
            dispatch(
                setAmbassadorSortItem({
                    options: item,
                }),
            )
        } else if (registrationViewType && campaignUuid) {
            dispatch(
                setSortItem({
                    viewType: registrationViewType,
                    campaignUuid,
                    options: item,
                }),
            )
        }

        setSelectedFilterAndSort({ field: item.field, isSort: true, hideTooltip })
    }

    const handleSortField = (option: OptionsState) => {
        const isOppositeSort = isOppositeSortField(option.value)

        const currentSortItem = {
            field: option.value as string,
            fieldLabel: option.label,
            // If sort doesn't already exist, sort by asc order by default
            // or by desc if option field value type is highest to lowest or latest to oldest
            sort:
                sortItem?.field === option.value && sortItem?.sort
                    ? sortItem.sort
                    : isOppositeSort
                      ? SORT_DIRECTION.DESC
                      : SORT_DIRECTION.ASC,
            sortDirectionLabel: '',
        }

        handleAddSortField(currentSortItem)
    }
    const onShowColumnPanel = (
        event: React.MouseEvent<HTMLButtonElement>,
        targetEl: HTMLElement,
        isMoreAction: boolean,
    ): void => {
        event.stopPropagation()
        const anchorEl = preferenceEl?.anchorEl !== null ? null : targetEl
        setPreferenceEl({ ...preferenceEl, anchorEl: anchorEl, isMoreAction: isMoreAction })
        apiRef.current.showPreferences(GridPreferencePanelsValue.columns)
    }
    const onClosePreferencePanel = (): void => {
        setPreferenceEl({ ...preferenceEl, anchorEl: null, isMoreAction: false })
    }

    const onShowColumnFilterTooltip = (event: any, value: string, isSort?: boolean): void => {
        event.stopPropagation()
        apiRef.current.hideColumnMenu()
        setSelectedFilterAndSort({ field: value, isSort: isSort })
    }

    const handleFilterChange = useCallback(
        (items: OptionsState[], column: FIELDS_TYPE) => {
            if (viewType === TableViewType.INDIVIDUAL_PERFORMANCE) {
                let updatedFilter = [...filters]
                items.forEach(item => {
                    const isItemExists = updatedFilter.some(
                        filterItem => filterItem.value === item.value && filterItem.columnField === column,
                    )
                    const newItem = {
                        columnField: column,
                        operatorValue: 'equals',
                        id: `${item.value}-${updatedFilter.length + 1}`,
                        value: item.value,
                        label: item.label,
                    }
                    if (isItemExists) {
                        updatedFilter = updatedFilter.filter(
                            filterItem => !(filterItem.value === item.value && filterItem.columnField === column),
                        )
                    } else {
                        updatedFilter.push(newItem)
                    }
                })
                dispatch(
                    setIndividualPerformanceFilterModel({
                        options: {
                            items: updatedFilter,
                            linkOperator: GridLinkOperator.And,
                            quickFilterLogicOperator: GridLinkOperator.And,
                            quickFilterValues: [],
                        },
                    }),
                )
            } else if (viewType === TableViewType.AMBASSADOR) {
                let updatedFilter = [...filters]
                items.forEach(item => {
                    const isItemExists = updatedFilter.some(
                        filterItem => filterItem.value === item.value && filterItem.columnField === column,
                    )
                    const newItem = {
                        columnField: column,
                        operatorValue: 'equals',
                        id: `${item.value}-${updatedFilter.length + 1}`,
                        value: item.value,
                        label: item.label,
                    }
                    if (isItemExists) {
                        updatedFilter = updatedFilter.filter(
                            filterItem => !(filterItem.value === item.value && filterItem.columnField === column),
                        )
                    } else {
                        updatedFilter.push(newItem)
                    }
                })
                dispatch(
                    setAmbassadorFilterModel({
                        options: {
                            items: updatedFilter,
                            linkOperator: GridLinkOperator.And,
                            quickFilterLogicOperator: GridLinkOperator.And,
                            quickFilterValues: [],
                        },
                    }),
                )
            } else if (campaignUuid && registrationViewType) {
                let updatedFilter = [...filters]
                items.forEach(item => {
                    const isItemExists = updatedFilter.some(
                        filterItem => filterItem.value === item.value && filterItem.columnField === column,
                    )
                    const newItem = {
                        columnField: column,
                        operatorValue: 'equals',
                        id: `${item.value}-${updatedFilter.length + 1}`,
                        value: item.value,
                        label: item.label,
                    }
                    if (isItemExists) {
                        updatedFilter = updatedFilter.filter(
                            filterItem => !(filterItem.value === item.value && filterItem.columnField === column),
                        )
                    } else {
                        updatedFilter.push(newItem)
                    }
                })
                dispatch(
                    setFilterModel({
                        viewType: registrationViewType,
                        campaignUuid,
                        options: {
                            items: updatedFilter,
                            linkOperator: GridLinkOperator.And,
                            quickFilterValues: [],
                            quickFilterLogicOperator: GridLinkOperator.And,
                        },
                    }),
                )
            }
        },
        [campaignUuid, registrationViewType, filters],
    )

    const handleRemoveFilter = (value: string, isSort?: boolean) => {
        if (viewType === TableViewType.INDIVIDUAL_PERFORMANCE) {
            if (isSort) {
                dispatch(
                    setIndividualPerformanceSortItem({
                        options: null,
                    }),
                )
            } else {
                dispatch(
                    setIndividualPerformanceFilterModel({
                        options: null,
                    }),
                )
                dispatch(setIndividualPerformanceFilterOptions([]))
            }
        } else if (viewType === TableViewType.AMBASSADOR) {
            if (isSort) {
                dispatch(
                    setAmbassadorSortItem({
                        options: null,
                    }),
                )
            } else {
                dispatch(
                    setAmbassadorFilterModel({
                        options: null,
                    }),
                )

                dispatch(setAmbassadorFilterOptions([]))
            }
        } else if (campaignUuid && registrationViewType) {
            if (isSort) {
                dispatch(
                    setSortItem({
                        viewType: registrationViewType,
                        campaignUuid,
                        options: null,
                    }),
                )
                return
            }
            dispatch(
                setFilterModel({
                    viewType: registrationViewType,
                    campaignUuid,
                    options: {
                        items: filters.filter(item => item.columnField !== value),
                        linkOperator: GridLinkOperator.And,
                        quickFilterValues: [],
                        quickFilterLogicOperator: GridLinkOperator.And,
                    },
                }),
            )
            dispatch(
                setFilterOptions({
                    viewType: registrationViewType,
                    campaignUuid,
                    options: filterOptions.filter(option => option.value !== value),
                }),
            )
        }
    }

    const onSelectionModelChange: DataGridProProps['onSelectionModelChange'] = selectionModel => {
        if (onClickCheckBox) {
            // use external mass action headerbar
            onClickCheckBox(apiRef, selectionModel)
        } else {
            if (toolBarType == 'waitlist') {
                setDisableMoveBackToPending(selectionModel.length <= 0)
                setDisableMoveToPrioriyList(selectionModel.length <= 0)
            } else if (toolBarType == 'moveBackToPending') {
                setDisableMoveBackToPending(selectionModel.length <= 0)
            } else {
                if (props.remainingSlot != undefined && props.remainingSlot <= 0) {
                    setDisableApprove(true)
                    setDisableMoveToWaitlist(selectionModel.length <= 0)
                } else {
                    setDisableApprove(props.remainingSlot == undefined || selectionModel.length <= 0)
                    setDisableMoveToWaitlist(true)
                }
            }
        }
    }

    useEffect(() => {
        onClosePreferencePanel()
        return (): void => {
            if (apiRef.current != null) {
                dispatch(setMuiTableRef(apiRef)) // for external component to interact with Table data
            }
        }
    }, [apiRef.current])

    return (
        <>
            {hideTableBorder ? (
                <TableWithHeaderBar
                    apiRef={apiRef}
                    hideToolbar={hideToolbar}
                    enableImportList={enableImportList}
                    enableMultiSelect={enableMultiSelect}
                    enableExport={enableExport}
                    showFilter={showFilter}
                    showSort={showSort}
                    disableApprove={disableApprove}
                    disableMoveToWaitlist={disableMoveToWaitlist}
                    disableMoveBackToPending={disableMoveBackToPending}
                    disableMoveToPrioriyList={disableMoveToPrioriyList}
                    hideApproveOrderButton={hideApproveOrderButton}
                    onShowColumnPanel={onShowColumnPanel}
                    onSelectionModelChange={onSelectionModelChange}
                    toolBarType={toolBarType}
                    noPadding={noPadding}
                    pinnedColumns={pinnedColumns}
                    hideFooter={hideFooter}
                    hideFooterRowCount={hideFooterRowCount}
                    preferenceEl={preferenceEl}
                    onClosePreferencePanel={onClosePreferencePanel}
                    filterOptions={filterOptions}
                    sortItem={sortItem}
                    selectedFilterAndSort={selectedFilterAndSort}
                    onRemoveFilter={handleRemoveFilter}
                    onShowColumnFilter={onShowColumnFilterTooltip}
                    onAddFilterField={handleAddFilterField}
                    onSelectFilter={handleFilterChange}
                    onSelectSort={handleAddSortField}
                    onCloseFilterField={() => setSelectedFilterAndSort({ field: '' })}
                    onAddSortField={handleSortField}
                    tableHeight={tableHeight}
                    viewType={viewType}
                    getRowClassName={getRowClassName}
                    headerHeight={headerHeight}
                    pinnedRows={pinnedRows}
                    customEmptyState={customEmptyState}
                    {...props}
                />
            ) : (
                <SubmissionContainer flexDirection={'column'} height={'-webkit-fill-available'}>
                    <TableWithHeaderBar
                        apiRef={apiRef}
                        hideToolbar={hideToolbar}
                        enableImportList={enableImportList}
                        enableMultiSelect={enableMultiSelect}
                        enableExport={enableExport}
                        showFilter={showFilter}
                        showSort={showSort}
                        disableApprove={disableApprove}
                        disableMoveToWaitlist={disableMoveToWaitlist}
                        disableMoveBackToPending={disableMoveBackToPending}
                        disableMoveToPrioriyList={disableMoveToPrioriyList}
                        hideApproveOrderButton={hideApproveOrderButton}
                        onShowColumnPanel={onShowColumnPanel}
                        onSelectionModelChange={onSelectionModelChange}
                        toolBarType={toolBarType}
                        noPadding={noPadding}
                        pinnedColumns={pinnedColumns}
                        hideFooter={hideFooter}
                        hideFooterRowCount={hideFooterRowCount}
                        preferenceEl={preferenceEl}
                        onClosePreferencePanel={onClosePreferencePanel}
                        filterOptions={filterOptions}
                        sortItem={sortItem}
                        onRemoveFilter={handleRemoveFilter}
                        onShowColumnFilter={onShowColumnFilterTooltip}
                        onAddFilterField={handleAddFilterField}
                        selectedFilterAndSort={selectedFilterAndSort}
                        onSelectFilter={handleFilterChange}
                        onSelectSort={handleAddSortField}
                        onCloseFilterField={() => setSelectedFilterAndSort({ field: '' })}
                        onAddSortField={handleSortField}
                        tableHeight={tableHeight}
                        getRowClassName={getRowClassName}
                        headerHeight={headerHeight}
                        viewType={viewType}
                        pinnedRows={pinnedRows}
                        customEmptyState={customEmptyState}
                        {...props}
                    />
                </SubmissionContainer>
            )}
        </>
    )
}

export default PartiTable
